/**
 * English — the source of truth for every other dictionary.
 *
 * Every other language's type is derived from this object, so adding a key here
 * and forgetting to translate it is a compile error rather than a blank label
 * in production.
 *
 * `{braces}` are interpolation slots. Keep them in translations.
 */
export const en = {
    // --- navigation -------------------------------------------------------
    'nav.find': 'Find',
    'nav.prayer': 'Prayer',
    'nav.cards': 'Cards',
    'nav.saved': 'Saved',
    'nav.main': 'Main',
    'nav.skip': 'Skip to content',

    // --- search and city --------------------------------------------------
    'search.placeholder': 'Search {city}',
    'search.label': 'Search places in {city}',
    'search.clear': 'Clear search',
    'search.matches': '{count} matches',
    'search.matches_one': '{count} match',
    'city.choose': 'Choose a city',
    'city.trust.certified': 'Certified',
    'city.trust.mixed': 'Part certified',
    'city.trust.community': 'Community',

    // --- filters -----------------------------------------------------------
    'filter.label': 'Filter results',
    'filter.eat': 'Eat',
    'filter.shop': 'Shop',
    'filter.pray': 'Pray',
    'filter.certifiedOnly': 'Certified only',
    'filter.hideExpired': 'Hide lapsed',
    'filter.nearMe': 'Within 2 km',
    'filter.clear': 'Clear',
    'filter.noneCertified': 'No certified listings in this city',
    'filter.noneExpired': 'Nothing here has a lapsed certificate',

    // --- results -----------------------------------------------------------
    'results.count': '{count} places',
    'results.count_one': '{count} place',
    'results.nearestFirst': ', nearest first',
    'results.loading': 'Loading {city}…',
    'results.error': '{city} could not be loaded. Check your connection and try again.',
    'results.emptyTitle': 'Nothing matches those filters.',
    'results.emptyBody': 'Try clearing a filter, or search a different name or area.',
    'results.clearAll': 'Clear all filters',
    'results.heading': 'Halal places in {city}',

    // --- trust banner -------------------------------------------------------
    'banner.community': 'Not certified.',
    'banner.communityBody':
        'These {count} listings come from {source}. No halal authority has checked them — ask staff before you order.',
    'banner.certified': '{certified} of {total} hold a current certificate',
    'banner.certifiedBody': ' from {source}.',
    'banner.lapsed': ' {count} has lapsed and is marked as such.',
    'banner.lapsed_other': ' {count} have lapsed and are marked as such.',
    'banner.mixedTail': ' The rest are community listings, not certified.',
    'banner.checked': 'Checked with {source} {when}.',

    // --- assurance ----------------------------------------------------------
    'assurance.certified': 'Halal certified',
    'assurance.certified.short': 'Certified',
    'assurance.certified.detail': 'The premises holds a halal certificate from a recognised certifier.',
    'assurance.certified_kitchen': 'Certified halal kitchen',
    'assurance.certified_kitchen.short': 'Certified kitchen',
    'assurance.certified_kitchen.detail': 'A dedicated certified halal kitchen inside a venue that also serves non-halal food.',
    'assurance.muslim_friendly': 'Halal friendly',
    'assurance.muslim_friendly.short': 'Friendly',
    'assurance.muslim_friendly.detail': 'Certified as serving no pork or alcohol. Not a fully halal premises.',
    'assurance.muslim_owned': 'Muslim owned',
    'assurance.muslim_owned.short': 'Muslim owned',
    'assurance.muslim_owned.detail': 'Muslim-owned, with no third-party certificate.',
    'assurance.self_declared': 'Self-declared halal',
    'assurance.self_declared.short': 'Self-declared',
    'assurance.self_declared.detail': 'The venue says it serves halal food. No certifier has checked it.',
    'assurance.community_reported': 'Community reported',
    'assurance.community_reported.short': 'Community',
    'assurance.community_reported.detail':
        'Mapped by OpenStreetMap contributors. Nobody has certified it — ask staff before you order.',

    // --- certificate --------------------------------------------------------
    'cert.label': 'Halal certificate',
    'cert.current': 'Certificate valid',
    'cert.expiring_soon': 'Expires soon',
    'cert.expired': 'Certificate expired',
    'cert.renewal_pending': 'Renewal in progress',
    'cert.listed': 'Listed as certified',
    'cert.delisted': 'Removed from the register',
    'cert.unknown': 'Status unknown',
    'cert.reference': 'Reference',
    'cert.category': 'Category',
    'cert.validUntil': 'Valid until',
    'cert.expiredOn': 'Expired',
    'cert.confirmed': 'Confirmed',
    'cert.noEndDate': 'no end date published',
    'cert.noCertifier': 'Certifier not recorded',
    'cert.expiredNote': 'This certificate has lapsed. It may since have been renewed — ask the venue before you order.',
    'cert.delistedNote': 'This venue is no longer on {certifier} register. Treat it as uncertified.',
    'cert.pendingNote': 'The register lists this certificate as being renewed.',
    'cert.openEndedNote':
        'This certifier publishes no expiry dates. A venue stays on the register until its certificate is withdrawn.',
    'cert.ariaValid': 'Valid until {date}',
    'cert.ariaExpired': 'Expired on {date}',
    'cert.ariaOpen': 'No expiry date is published. Confirmed {when}.',

    // --- place detail --------------------------------------------------------
    'place.back': 'Back to list',
    'place.save': 'Save',
    'place.saved': 'Saved',
    'place.address': 'Address',
    'place.hours': 'Hours',
    'place.phone': 'Phone',
    'place.directions': 'Directions',
    'place.website': 'Website',
    'place.share': 'Share',
    'place.listedBy': 'Listed by {source}',
    'place.checkedWhen': 'Certificate checked {when}',
    'place.approximate': 'Approximate location — pinned to {district} because the address could not be resolved to a building.',
    'place.theDistrict': 'the district',
    'place.away': '{distance} away',

    // --- venue types ----------------------------------------------------------
    'type.restaurant': 'Restaurant',
    'type.cafe': 'Café',
    'type.bakery': 'Bakery',
    'type.fast_food': 'Fast food',
    'type.food_court': 'Food court',
    'type.hotel_restaurant': 'Hotel restaurant',
    'type.grocery': 'Grocery',
    'type.butcher': 'Butcher',
    'type.mosque': 'Mosque',
    'type.prayer_room': 'Prayer room',
    'type.attraction_dining': 'Attraction dining',

    // --- prayer ----------------------------------------------------------------
    'prayer.title': 'Prayer',
    'prayer.needLocation': 'Allow location access to get prayer times and the direction of prayer for where you are.',
    'prayer.next': 'Next',
    'prayer.fajr': 'Fajr',
    'prayer.sunrise': 'Sunrise',
    'prayer.dhuhr': 'Dhuhr',
    'prayer.asr': 'Asr',
    'prayer.maghrib': 'Maghrib',
    'prayer.isha': 'Isha',
    'prayer.inHours': 'in {hours}h {minutes}m',
    'prayer.inMinutes': 'in {minutes} min',
    'prayer.imminent': 'in under a minute',
    'prayer.highLatitude':
        'At this latitude the sun does not dip far enough below the horizon for Fajr and Isha to be calculated conventionally today. Follow your local mosque’s timetable.',
    'prayer.method': 'Calculation method',
    'prayer.methodLabel': 'Method',
    'prayer.asrLabel': 'Asr',
    'prayer.asrStandard': 'Standard (Shafi’i, Maliki, Hanbali)',
    'prayer.asrHanafi': 'Hanafi',
    'prayer.calcNote':
        'Calculated on your device from your location, so it works offline. Times are a guide — follow your local mosque where they differ.',

    // --- qibla ------------------------------------------------------------------
    'qibla.title': 'Qibla',
    'qibla.fromNorth': 'from north',
    'qibla.aria': 'Qibla is {degrees} degrees from true north',
    'qibla.needsPermission': 'This device needs permission before it can act as a compass.',
    'qibla.usecompass': 'Use device compass',
    'qibla.noCompass': 'No compass reading here. Face {degrees}° from true north using a compass.',

    // --- dietary cards -------------------------------------------------------------
    'cards.title': 'Show this to staff',
    'cards.type': 'Card type',
    'cards.language': 'Language',
    'cards.halal': 'Halal',
    'cards.vegetarian': 'Vegetarian',
    'cards.allergies': 'Allergies',
    'cards.hint': 'Turn your screen brightness up and hand the phone over.',

    // --- saved ----------------------------------------------------------------------
    'saved.title': 'Saved',
    'saved.emptyTitle': 'Nothing saved yet.',
    'saved.emptyBody': 'Tap the bookmark on any place to keep it here — it stays available offline.',
    'saved.emptyCta': 'Find somewhere to eat',
    'saved.staleOne': '{count} saved place has a certificate that is no longer current.',
    'saved.staleOther': '{count} saved places have a certificate that is no longer current.',
    'saved.staleBody': ' Certificates lapse after you save them — check before you go.',
    'saved.remove': 'Remove {name} from saved',

    // --- language picker ---------------------------------------------------------------
    'lang.choose': 'Choose a language',
    'lang.label': 'Language',

    // --- relative time -------------------------------------------------------------------
    'time.today': 'today',
    'time.yesterday': 'yesterday',
    'time.daysAgo': '{count} days ago',
    'time.on': 'on {date}',
} as const;

export type TranslationKey = keyof typeof en;
export type Dictionary = Record<TranslationKey, string>;
